import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Popover, IconButton, Typography, Button, Paper } from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';

interface CustomDatePickerProps {
  label?: string;
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  minDate?: string; // Minimum date allowed (YYYY-MM-DD)
  maxDate?: string; // Maximum date allowed (YYYY-MM-DD)
  eventDates?: string[]; // Array of dates (YYYY-MM-DD) that have events
  eventCountMap?: Record<string, number>; // Map of date to event count (YYYY-MM-DD -> count)
  inline?: boolean; // If true, displays calendar inline without TextField/Popover
  showTodayButton?: boolean;
  showClearButton?: boolean;
  initialDate?: string; // Initial date to display calendar (YYYY-MM-DD), defaults to today
  disableDatesByRange?: boolean; // If true, disable dates outside minDate/maxDate range. Defaults to true
  hideOtherMonthDates?: boolean; // If true, hide dates from other months instead of dimming them
}

interface DayInfo {
  date: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  fullDate: Date;
  dayOfWeek?: number;
  isDisabled?: boolean;
  hasEvent?: boolean;
}

interface YearMonthPickerProps {
  pickerView: 'year' | 'month';
  currentDate: Date;
  selectedYear: number;
  inline: boolean;
  monthNames: string[];
  minDate?: string;
  maxDate?: string;
  onBack: () => void;
  onYearSelect: (year: number) => void;
  onMonthSelect: (month: number) => void;
}

interface CalendarContentProps {
  inline: boolean;
  currentDate: Date;
  monthNames: string[];
  dayNames: string[];
  days: DayInfo[];
  eventDates: string[];
  eventCountMap?: Record<string, number>;
  showClearButton: boolean;
  showTodayButton: boolean;
  hideOtherMonthDates: boolean;
  disabled?: boolean;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onYearMonthClick: () => void;
  onDateClick: (day: DayInfo) => void;
  onToday: () => void;
  onClear: () => void;
  formatDateToString: (date: Date) => string;
  getDayTextColor: (day: DayInfo) => string;
  getHoverBackgroundColor: (day: DayInfo) => string;
  getHoverBorder: (day: DayInfo) => string;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
}

interface CalendarPickerContentProps {
  showYearMonthPicker: boolean;
  pickerView: 'year' | 'month';
  currentDate: Date;
  selectedYear: number;
  inline: boolean;
  monthNames: string[];
  dayNames: string[];
  days: DayInfo[];
  eventDates: string[];
  eventCountMap?: Record<string, number>;
  showClearButton: boolean;
  showTodayButton: boolean;
  hideOtherMonthDates: boolean;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  onBack: () => void;
  onYearSelect: (year: number) => void;
  onMonthSelect: (month: number) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onYearMonthClick: () => void;
  onDateClick: (day: DayInfo) => void;
  onToday: () => void;
  onClear: () => void;
  formatDateToString: (date: Date) => string;
  getDayTextColor: (day: DayInfo) => string;
  getHoverBackgroundColor: (day: DayInfo) => string;
  getHoverBorder: (day: DayInfo) => string;
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
}

// Format date to YYYY-MM-DD in local timezone
const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to determine day text color
const getDayTextColor = (day: DayInfo): string => {
  if (!day.isCurrentMonth) return '#cccccc';
  if (day.isSelected) return '#ffffff';
  if (day.dayOfWeek === 0) return '#d32f2f'; // Sunday - Red
  if (day.dayOfWeek === 6) return '#1976d2'; // Saturday - Blue
  return '#333333';
};

// Helper function to get hover background color
const getHoverBackgroundColor = (day: DayInfo): string => {
  if (day.isDisabled) return 'transparent';
  if (day.isSelected) return '#0047BA';
  if (day.isToday || day.isCurrentMonth) return '#e3f2fd';
  return 'transparent';
};

// Helper function to get hover border
const getHoverBorder = (day: DayInfo): string => {
  if (day.isDisabled || day.isSelected) return 'none';
  if (day.isToday || day.isCurrentMonth) return '1px solid #0047BA';
  return 'none';
};

// Helper function to get background color for year/month picker buttons
const getPickerButtonBackgroundColor = (
  isDisabled: boolean,
  isSelected: boolean,
  defaultColor: string
): string => {
  if (isDisabled) return 'transparent';
  if (isSelected) return '#0047BA';
  return defaultColor;
};

// Helper function to get text color for year/month picker buttons
const getPickerButtonColor = (isDisabled: boolean, isSelected: boolean): string => {
  if (isDisabled) return '#cccccc';
  if (isSelected) return '#ffffff';
  return '#333333';
};

// Generate years for year picker (15 years range: currentYear - 7 to currentYear + 7)
const generateYears = (currentDate: Date): number[] => {
  const currentYear = currentDate.getFullYear();
  const startYear = currentYear - 7;
  const years = [];
  for (let year = startYear; year <= startYear + 14; year++) {
    years.push(year);
  }
  return years;
};

// Year/Month Picker Component
const YearMonthPicker: React.FC<YearMonthPickerProps> = ({
  pickerView,
  currentDate,
  selectedYear,
  inline,
  monthNames,
  minDate,
  maxDate,
  onBack,
  onYearSelect,
  onMonthSelect,
}) => {
  // Check if a year is disabled (outside minDate/maxDate range)
  const isYearDisabled = (year: number): boolean => {
    if (!minDate && !maxDate) return false;

    // Check if year is before minDate year
    if (minDate) {
      const minYear = new Date(minDate).getFullYear();
      if (year < minYear) return true;
    }

    // Check if year is after maxDate year
    if (maxDate) {
      const maxYear = new Date(maxDate).getFullYear();
      if (year > maxYear) return true;
    }

    return false;
  };

  // Check if a month is disabled (outside minDate/maxDate range)
  const isMonthDisabled = (year: number, month: number): boolean => {
    if (!minDate && !maxDate) return false;

    // Check if month is before minDate
    if (minDate) {
      const minDateObj = new Date(minDate);
      const minYear = minDateObj.getFullYear();
      const minMonth = minDateObj.getMonth();
      // Check if this year/month combination is before minDate
      if (year < minYear || (year === minYear && month < minMonth)) {
        return true;
      }
    }

    // Check if month is after maxDate
    if (maxDate) {
      const maxDateObj = new Date(maxDate);
      const maxYear = maxDateObj.getFullYear();
      const maxMonth = maxDateObj.getMonth();
      // Check if this year/month combination is after maxDate
      if (year > maxYear || (year === maxYear && month > maxMonth)) {
        return true;
      }
    }

    return false;
  };

  if (pickerView === 'year') {
    const years = generateYears(currentDate);
    const currentYear = currentDate.getFullYear();

    return (
      <Paper
        sx={{
          p: inline ? 0 : 1.5,
          minWidth: 280,
          maxWidth: 300,
          boxShadow: inline ? 'none' : undefined,
          border: inline ? 'none' : undefined,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <IconButton
            onClick={onBack}
            size='small'
            sx={{ p: 0.5 }}
            data-testid='year-picker-back-button'
          >
            <ChevronLeftIcon fontSize='small' />
          </IconButton>
          <Typography
            variant='subtitle2'
            sx={{ color: '#333333', fontWeight: 'bold', fontSize: '1rem' }}
            data-testid='year-picker-title'
          >
            年を選択
          </Typography>
          <Box sx={{ width: 40 }} /> {/* Spacer for centering */}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {years.map((year) => {
            const isSelected = year === currentYear;
            const isDisabled = isYearDisabled(year);
            const buttonColor = getPickerButtonColor(isDisabled, isSelected);
            const buttonBackgroundColor = getPickerButtonBackgroundColor(
              isDisabled,
              isSelected,
              'transparent'
            );
            const hoverBackgroundColor = getPickerButtonBackgroundColor(
              isDisabled,
              isSelected,
              '#e3f2fd'
            );
            return (
              <Button
                key={year}
                onClick={() => !isDisabled && onYearSelect(year)}
                disabled={isDisabled}
                sx={{
                  width: 'calc(33.33% - 4px)',
                  minWidth: 0,
                  height: 40,
                  p: 0,
                  fontSize: '0.875rem',
                  color: buttonColor,
                  backgroundColor: buttonBackgroundColor,
                  borderRadius: '8px',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.4 : 1,
                  '&:hover': {
                    backgroundColor: hoverBackgroundColor,
                  },
                  '&.Mui-disabled': {
                    color: '#cccccc',
                  },
                }}
              >
                {year}
              </Button>
            );
          })}
        </Box>
      </Paper>
    );
  }

  // Month picker view
  return (
    <Paper
      sx={{
        p: inline ? 0 : 1.5,
        minWidth: 280,
        maxWidth: 300,
        boxShadow: inline ? 'none' : undefined,
        border: inline ? 'none' : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton
          onClick={onBack}
          size='small'
          sx={{ p: 0.5 }}
          data-testid='month-picker-back-button'
        >
          <ChevronLeftIcon fontSize='small' />
        </IconButton>
        <Typography
          variant='subtitle2'
          sx={{ color: '#333333', fontWeight: 'bold', fontSize: '1rem' }}
          data-testid='month-picker-title'
        >
          {selectedYear}年
        </Typography>
        <Box sx={{ width: 40 }} />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {monthNames.map((month, index) => {
          const isSelected =
            index === currentDate.getMonth() && selectedYear === currentDate.getFullYear();
          const isDisabled = isMonthDisabled(selectedYear, index);
          const buttonColor = getPickerButtonColor(isDisabled, isSelected);
          const buttonBackgroundColor = getPickerButtonBackgroundColor(
            isDisabled,
            isSelected,
            'transparent'
          );
          const hoverBackgroundColor = getPickerButtonBackgroundColor(
            isDisabled,
            isSelected,
            '#e3f2fd'
          );
          return (
            <Button
              key={month}
              onClick={() => !isDisabled && onMonthSelect(index)}
              disabled={isDisabled}
              sx={{
                width: 'calc(33.33% - 4px)',
                minWidth: 0,
                height: 40,
                p: 0,
                fontSize: '0.875rem',
                color: buttonColor,
                backgroundColor: buttonBackgroundColor,
                borderRadius: '8px',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.4 : 1,
                '&:hover': {
                  backgroundColor: hoverBackgroundColor,
                },
                '&.Mui-disabled': {
                  color: '#cccccc',
                },
              }}
            >
              {month}
            </Button>
          );
        })}
      </Box>
    </Paper>
  );
};

// Calendar content component
const CalendarContent: React.FC<CalendarContentProps> = ({
  inline,
  currentDate,
  monthNames,
  dayNames,
  days,
  eventDates,
  eventCountMap,
  showClearButton,
  showTodayButton,
  hideOtherMonthDates,
  disabled = false,
  onPrevMonth,
  onNextMonth,
  onYearMonthClick,
  onDateClick,
  onToday,
  onClear,
  formatDateToString,
  getDayTextColor,
  getHoverBackgroundColor,
  getHoverBorder,
  canNavigatePrev,
  canNavigateNext,
}) => (
  <Paper
    sx={{
      p: inline ? 0 : 1.5,
      minWidth: 280,
      maxWidth: 300,
      boxShadow: inline ? 'none' : undefined,
      border: inline ? 'none' : undefined,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <IconButton onClick={onPrevMonth} size='small' sx={{ p: 0.5 }} disabled={!canNavigatePrev}>
        <ChevronLeftIcon fontSize='small' />
      </IconButton>
      <Box
        onClick={onYearMonthClick}
        data-testid='year-month-header'
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.7,
          },
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{ color: '#333333', fontWeight: 'bold', fontSize: '1rem' }}
        >
          {currentDate.getFullYear()}年 {monthNames[currentDate.getMonth()]}
        </Typography>
        <KeyboardArrowUpIcon fontSize='small' sx={{ color: '#666666', fontSize: '1rem' }} />
      </Box>
      <IconButton onClick={onNextMonth} size='small' sx={{ p: 0.5 }} disabled={!canNavigateNext}>
        <ChevronRightIcon fontSize='small' />
      </IconButton>
    </Box>

    {/* Days of week */}
    <Box sx={{ display: 'flex', mb: 1 }}>
      {dayNames.map((day, index) => {
        // Set color for weekend days
        let headerColor = '#666666';
        if (index === 0) {
          // Sunday - Red
          headerColor = '#d32f2f';
        } else if (index === 6) {
          // Saturday - Blue
          headerColor = '#1976d2';
        }

        return (
          <Box key={day} sx={{ flex: 1, textAlign: 'center' }}>
            <Typography
              variant='caption'
              sx={{
                fontWeight: 'bold',
                color: headerColor,
                fontSize: '0.875rem',
                py: 0.5,
              }}
            >
              {day}
            </Typography>
          </Box>
        );
      })}
    </Box>

    {/* Calendar grid */}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {days.map((day, index) => {
        const textColor = day.isDisabled ? '#cccccc' : getDayTextColor(day);

        // Hide dates from other months if hideOtherMonthDates is true
        if (hideOtherMonthDates && !day.isCurrentMonth) {
          return (
            <Box
              key={`day-${index}-${day.date}`}
              sx={{ width: 'calc(14.28% - 4px)', textAlign: 'center' }}
            />
          );
        }

        return (
          <Box
            key={`day-${index}-${day.date}`}
            sx={{ width: 'calc(14.28% - 4px)', textAlign: 'center' }}
          >
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Button
                onClick={() => !disabled && onDateClick(day)}
                disabled={day.isDisabled}
                sx={{
                  minWidth: inline ? 34 : 28,
                  height: inline ? 34 : 28,
                  p: 0,
                  fontSize: inline ? '0.875rem' : '0.75rem',
                  color: textColor,
                  backgroundColor: day.isSelected ? '#0047BA' : 'transparent',
                  '&:hover': {
                    backgroundColor: getHoverBackgroundColor(day),
                    border: getHoverBorder(day),
                  },
                  fontWeight: day.isToday ? 'bold' : 'normal',
                  border: day.isToday ? '1px solid #0047BA' : 'none',
                  borderRadius: '50%',
                  cursor: day.isDisabled ? 'not-allowed' : 'pointer',
                  opacity: day.isDisabled ? 0.4 : 1,
                  '&.Mui-disabled': {
                    color: '#cccccc',
                    opacity: 0.4,
                  },
                }}
              >
                {day.date}
              </Button>

              {/* Event dots indicator */}
              {day.hasEvent && day.isCurrentMonth && eventDates.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: inline ? 0 : -2,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: '2px',
                    justifyContent: 'center',
                  }}
                >
                  {(() => {
                    const dateStr = formatDateToString(day.fullDate);
                    // Use eventCountMap if provided, otherwise fallback to counting in eventDates
                    const eventCount =
                      eventCountMap?.[dateStr] ?? eventDates.filter((d) => d === dateStr).length;
                    const dots = Math.min(eventCount, 3);
                    return Array.from({ length: dots }).map((_, dotIndex) => (
                      <Box
                        key={`dot-${index}-${dotIndex}`}
                        sx={{
                          width: inline ? 5 : 4,
                          height: inline ? 5 : 4,
                          borderRadius: '50%',
                          backgroundColor: day.isSelected ? '#ffffff' : '#0047BA',
                        }}
                      />
                    ));
                  })()}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>

    {/* Action buttons */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
      {showClearButton && (
        <Button
          onClick={onClear}
          size='small'
          color='inherit'
          sx={{ fontSize: '0.75rem', p: 0.5, color: '#666666' }}
        >
          クリア
        </Button>
      )}
      {showTodayButton && (
        <Button
          onClick={onToday}
          size='small'
          sx={{ color: '#0047BA', fontSize: '0.75rem', p: 0.5 }}
        >
          今日
        </Button>
      )}
    </Box>
  </Paper>
);

// Common component to render calendar picker content (YearMonthPicker or CalendarContent)
const CalendarPickerContent: React.FC<CalendarPickerContentProps> = ({
  showYearMonthPicker,
  pickerView,
  currentDate,
  selectedYear,
  inline,
  monthNames,
  dayNames,
  days,
  eventDates,
  eventCountMap,
  showClearButton,
  showTodayButton,
  hideOtherMonthDates,
  disabled,
  minDate,
  maxDate,
  onBack,
  onYearSelect,
  onMonthSelect,
  onPrevMonth,
  onNextMonth,
  onYearMonthClick,
  onDateClick,
  onToday,
  onClear,
  formatDateToString,
  getDayTextColor,
  getHoverBackgroundColor,
  getHoverBorder,
  canNavigatePrev,
  canNavigateNext,
}) => {
  if (showYearMonthPicker) {
    return (
      <YearMonthPicker
        pickerView={pickerView}
        currentDate={currentDate}
        selectedYear={selectedYear}
        inline={inline}
        monthNames={monthNames}
        minDate={minDate}
        maxDate={maxDate}
        onBack={onBack}
        onYearSelect={onYearSelect}
        onMonthSelect={onMonthSelect}
      />
    );
  }

  return (
    <CalendarContent
      inline={inline}
      currentDate={currentDate}
      monthNames={monthNames}
      dayNames={dayNames}
      days={days}
      eventDates={eventDates}
      eventCountMap={eventCountMap}
      showClearButton={showClearButton}
      showTodayButton={showTodayButton}
      hideOtherMonthDates={hideOtherMonthDates}
      disabled={disabled}
      onPrevMonth={onPrevMonth}
      onNextMonth={onNextMonth}
      onYearMonthClick={onYearMonthClick}
      onDateClick={onDateClick}
      onToday={onToday}
      onClear={onClear}
      formatDateToString={formatDateToString}
      getDayTextColor={getDayTextColor}
      getHoverBackgroundColor={getHoverBackgroundColor}
      getHoverBorder={getHoverBorder}
      canNavigatePrev={canNavigatePrev}
      canNavigateNext={canNavigateNext}
    />
  );
};

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  label = '',
  value,
  onChange,
  placeholder = '選択してください',
  error = false,
  helperText,
  disabled = false,
  minDate,
  maxDate,
  eventDates = [],
  eventCountMap,
  inline = false,
  showTodayButton = true,
  showClearButton = true,
  initialDate,
  disableDatesByRange = true,
  hideOtherMonthDates = false,
}) => {
  const [open, setOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(initialDate ? new Date(initialDate) : new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(value ? new Date(value) : null);
  const [showYearMonthPicker, setShowYearMonthPicker] = useState(false);
  const [pickerView, setPickerView] = useState<'year' | 'month'>('year');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const anchorRef = useRef<HTMLDivElement>(null);

  // Month names in Japanese
  const monthNames = [
    '1月',
    '2月',
    '3月',
    '4月',
    '5月',
    '6月',
    '7月',
    '8月',
    '9月',
    '10月',
    '11月',
    '12月',
  ];

  const dayNames = ['日', '月', '火', '水', '木', '金', '土'];

  // Sync selectedDate with value prop
  useEffect(() => {
    if (value) {
      setSelectedDate(new Date(value));
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  // Sync currentDate with initialDate prop
  useEffect(() => {
    if (initialDate) {
      const newDate = new Date(initialDate);
      setCurrentDate(newDate);
    }
  }, [initialDate]);

  // Get days in month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Previous month days
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const fullDate = new Date(year, month - 1, prevMonth.getDate() - i);
      days.push({
        date: prevMonth.getDate() - i,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        fullDate,
        hasEvent: hasEvent(fullDate),
      });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const fullDate = new Date(year, month, day);
      const isToday = fullDate.toDateString() === new Date().toDateString();
      const isSelected = !!(
        selectedDate && fullDate.toDateString() === selectedDate.toDateString()
      );
      const dayOfWeek = fullDate.getDay(); // 0 = Sunday, 6 = Saturday
      const isDisabled = isDateDisabled(fullDate);

      days.push({
        date: day,
        isCurrentMonth: true,
        isToday,
        isSelected,
        fullDate,
        dayOfWeek,
        isDisabled,
        hasEvent: hasEvent(fullDate),
      });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const fullDate = new Date(year, month + 1, day);
      days.push({
        date: day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        fullDate,
        hasEvent: hasEvent(fullDate),
      });
    }

    return days;
  };

  // Check if date has event
  const hasEvent = (date: Date): boolean => {
    const dateStr = formatDateToString(date);
    return eventDates.includes(dateStr);
  };

  // Check if date is disabled
  const isDateDisabled = (date: Date): boolean => {
    if (!disableDatesByRange) return false;
    const dateStr = formatDateToString(date);
    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  };

  // Check if previous month navigation is allowed
  const canNavigatePrevMonth = (): boolean => {
    if (!minDate) return true;
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    // Check if the first day of previous month is before minDate
    const minDateObj = new Date(minDate);
    const minYear = minDateObj.getFullYear();
    const minMonth = minDateObj.getMonth();
    return (
      prevMonth.getFullYear() > minYear ||
      (prevMonth.getFullYear() === minYear && prevMonth.getMonth() >= minMonth)
    );
  };

  // Check if next month navigation is allowed
  const canNavigateNextMonth = (): boolean => {
    if (!maxDate) return true;
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    // Check if the first day of next month is after maxDate
    const maxDateObj = new Date(maxDate);
    const maxYear = maxDateObj.getFullYear();
    const maxMonth = maxDateObj.getMonth();
    return (
      nextMonth.getFullYear() < maxYear ||
      (nextMonth.getFullYear() === maxYear && nextMonth.getMonth() <= maxMonth)
    );
  };

  const handleDateClick = (day: DayInfo) => {
    if (day.isCurrentMonth && !day.isDisabled) {
      setSelectedDate(day.fullDate);
      onChange(formatDateToString(day.fullDate));
      setOpen(false);
    }
  };

  const handlePrevMonth = () => {
    if (canNavigatePrevMonth()) {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    }
  };

  const handleNextMonth = () => {
    if (canNavigateNextMonth()) {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    }
  };

  const handleToday = () => {
    const today = new Date();
    if (!isDateDisabled(today)) {
      setSelectedDate(today);
      onChange(formatDateToString(today));
      setOpen(false);
    }
  };

  const handleClear = () => {
    setSelectedDate(null);
    onChange('');
    setOpen(false);
  };

  const handleYearMonthClick = () => {
    setShowYearMonthPicker(true);
    setPickerView('year');
    setSelectedYear(currentDate.getFullYear());
  };

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setPickerView('month');
  };

  const handleMonthSelect = (month: number) => {
    const newDate = new Date(selectedYear, month, 1);
    setCurrentDate(newDate);
    setShowYearMonthPicker(false);
    setPickerView('year');
  };

  // Reset year/month picker when popover closes
  useEffect(() => {
    if (!open) {
      setShowYearMonthPicker(false);
      setPickerView('year');
    }
  }, [open]);

  const handleYearMonthPickerBack = () => {
    if (pickerView === 'month') {
      setPickerView('year');
    } else {
      setShowYearMonthPicker(false);
    }
  };

  const formatDisplayValue = (date: Date | null) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const days = getDaysInMonth(currentDate);

  const renderCalendarContent = () => {
    // Disable navigation buttons when Year/Month picker is open
    const canNavigatePrev = !showYearMonthPicker && canNavigatePrevMonth();
    const canNavigateNext = !showYearMonthPicker && canNavigateNextMonth();

    return (
      <CalendarPickerContent
        showYearMonthPicker={showYearMonthPicker}
        pickerView={pickerView}
        currentDate={currentDate}
        selectedYear={selectedYear}
        inline={inline}
        monthNames={monthNames}
        dayNames={dayNames}
        days={days}
        eventDates={eventDates}
        eventCountMap={eventCountMap}
        showClearButton={showClearButton}
        showTodayButton={showTodayButton}
        hideOtherMonthDates={hideOtherMonthDates}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        onBack={handleYearMonthPickerBack}
        onYearSelect={handleYearSelect}
        onMonthSelect={handleMonthSelect}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onYearMonthClick={handleYearMonthClick}
        onDateClick={handleDateClick}
        onToday={handleToday}
        onClear={handleClear}
        formatDateToString={formatDateToString}
        getDayTextColor={getDayTextColor}
        getHoverBackgroundColor={getHoverBackgroundColor}
        getHoverBorder={getHoverBorder}
        canNavigatePrev={canNavigatePrev}
        canNavigateNext={canNavigateNext}
      />
    );
  };

  // If inline mode, render calendar directly with year/month picker support
  if (inline) {
    return <Box>{renderCalendarContent()}</Box>;
  }

  // Otherwise, render with TextField and Popover
  return (
    <Box>
      <TextField
        ref={anchorRef}
        label={label}
        value={formatDisplayValue(selectedDate)}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        disabled={disabled}
        size='small'
        onClick={() => !disabled && setOpen(true)}
        slotProps={{
          inputLabel: {
            shrink: open || !!selectedDate,
          },
          input: {
            readOnly: true,
            endAdornment: (
              <IconButton
                size='small'
                onClick={() => !disabled && setOpen(true)}
                disabled={disabled}
                sx={{ p: 0.5 }}
              >
                <CalendarIcon fontSize='small' color='action' />
              </IconButton>
            ),
          },
        }}
        sx={{
          '& .MuiInputBase-root': {
            display: 'flex',
            flexWrap: 'nowrap',
          },

          '& .MuiInputBase-input': {
            minWidth: 0,

            flexShrink: 1,

            flexBasis: 'auto',

            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',

            color: '#333333',
            cursor: disabled ? 'default' : 'pointer',
            fontSize: '0.875rem',
          },

          '& .MuiInputAdornment-root': {
            flexShrink: 0,
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#999999',
            opacity: 1,
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            color: '#666666',
            maxWidth: 'calc(100% - 50px)',
            paddingRight: '8px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },

          '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            maxWidth: '100%',
            paddingRight: 0,
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: 1.5,
              boxShadow: '0 2px 12px rgba(38, 10, 202, 0.1)',
            },
          },
        }}
      >
        {renderCalendarContent()}
      </Popover>
    </Box>
  );
};

export default CustomDatePicker;
